import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import { Table } from 'reactstrap';

import UsersApi from 'apis/Users';
import EmailField from 'components/users/details/EmailField';
import NameField from 'components/users/details/NameField';
import ManageSubscription from 'components/subscription/manageSubscription';
import {
  isProPlan,
  isEssentialPlan,
  isPlanActive,
  isSuperAdmin,
} from 'utils/plansHelper';
import UpdateEmail from 'components/ConfirmationModal/Modal/UpdateEmail';
import EmailConfirmationSent from 'components/ConfirmationModal/Modal/EmailConfirmationSent';
import Toaster from 'components/Toaster';
import {
  insertIdtoUrl,
  ROUTES,
  RENEW_SUBSCRIPTION_ROUTES,
} from 'constants/Routes';
import Loading from 'utils/Loading';
import { getNameInitials } from 'utils/trimName';
import { formatUserDetailDate, getTimeSince } from 'utils/date';
import 'assets/scss/business.css';
import 'assets/scss/table.css';
import 'assets/scss/userDashboard.css';
import { getPlanSubscriptionStatus } from 'constants/localStorageManager';
import BusinessCardWrapper from '../components/businessDetail/businessCardWrapper';
import FormField from 'components/Form/FormField';

const Field = ({ value, label, icon }) => {
  const isEditIcon = false;
  const editFaIcon = <FontAwesomeIcon icon="pencil-alt" />;
  return (
    <div className="row">
      <div className="col-md-4 row-title mb-md-0 mb-2">
        <FontAwesomeIcon icon={icon} /> {label}
      </div>
      <div className="col-md-8">
        <div className="d-flex align-items-center justify-content-between">
          <div>{value}</div>
          <div>{isEditIcon ? editFaIcon : ' '}</div>
        </div>
      </div>
    </div>
  );
};

const CategoriesList = ({ categories }) => (
  <div className="p-3 business-badge">
    {categories.map(category => (
      <span className="badge">{category.name}</span>
    ))}
  </div>
);

CategoriesList.defaultProps = {
  categories: [],
};

CategoriesList.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
    })
  ),
};

class UserDetails extends Component {
  constructor() {
    super();
    this.state = {
      toggleEmailInput: false,
      toggleNameInput: false,
      user: {
        business: {},
        plan_subscription: {},
        plan: {},
      },
      isLoading: true,
      formValues: {},
      emailConfirmationVisible: false,
      updateEmail: '',
    };
  }

  componentDidMount = () => {
    const {
      match: { params },
    } = this.props;
    const { id } = params;

    if (id) {
      this.fetchUserDetails(id);
    } else {
      this.fetchMyDetails();
    }
  };

  fetchUserDetails = _id => {
    this.setState({ isLoading: true });
    if (!_id) return this.fetchMyDetails();
    UsersApi.getDetails(_id)
      .then(({ data }) => {
        const {
          id,
          first_name: firstName,
          last_name: lastName,
          middle_name: middleName,
          email: editEmail,
        } = data;
        this.setState({
          user: data,
          isLoading: false,
          formValues: {
            id,
            firstName,
            middleName,
            lastName,
            editEmail,
          },
        });
      })
      .catch(() => {
        this.setState({ isLoading: true });
        Toaster.getErrorToaster(
          'exclamation-triangle',
          'Error fetching information'
        );
      });
  };

  fetchMyDetails = () => {
    UsersApi.getMyDetails()
      .then(({ data }) => {
        const {
          id,
          first_name: firstName,
          last_name: lastName,
          middle_name: middleName,
          email: editEmail,
        } = data;
        this.setState({
          user: data,
          isLoading: false,
          formValues: {
            id,
            firstName,
            middleName,
            lastName,
            editEmail,
          },
        });
      })
      .catch(() => {
        this.setState({ isLoading: true });
        Toaster.getErrorToaster(
          'exclamation-triangle',
          'Error fetching information'
        );
      });
  };

  handleChange = e => {
    const { name, value } = e.target;
    const { formValues } = this.state;
    const editValues = { ...formValues };
    editValues[name] = value;
    this.setState({
      formValues: { ...editValues },
    });
  };

  updateName = () => {
    const { formValues } = this.state;
    const { id, firstName, middleName, lastName } = formValues;

    const user = {
      first_name: firstName,
      middle_name: middleName,
      last_name: lastName,
    };

    UsersApi.updateUserInfo(id, user)
      .then(({ data }) => {
        Toaster.getSuccessToaster(
          'check-circle',
          'Successfully updated information'
        );
        this.setState({ toggleNameInput: false });
        this.fetchUserDetails(data.id);
      })
      .catch(() => {
        Toaster.getErrorToaster(
          'exclamation-triangle',
          'Failed to update information'
        );
      });
  };

  updateEmail = () => {
    const { formValues } = this.state;
    const { id, editEmail: email } = formValues;

    const user = {
      email,
    };

    UsersApi.updateUserInfo(id, user)
      .then(() => {
        this.setState({ toggleEmailInput: false });
        this.fetchUserDetails();
      })
      .catch(() => {
        Toaster.getErrorToaster(
          'exclamation-triangle',
          'Failed to update email'
        );
      });
  };

  handleNameEdit = () => {
    this.setState({
      toggleNameInput: true,
    });
  };

  handleEmailEdit = () => {
    this.setState({
      toggleEmailInput: true,
    });
  };

  handleEmailToggle = () => {
    this.setState(({ toggleEmailInput, formValues, user }) => ({
      toggleEmailInput: !toggleEmailInput,
      formValues: {
        ...formValues,
        editEmail: user.email,
      },
    }));
  };

  handleNameToggle = () => {
    this.setState(({ toggleNameInput, formValues, user }) => ({
      toggleNameInput: !toggleNameInput,
      formValues: {
        ...formValues,
        firstName: user.first_name,
        middleName: user.middle_name,
        lastName: user.last_name,
      },
    }));
  };

  handleEmailConfirmationSent = email => {
    this.setState(({ emailConfirmationVisible }) => ({
      emailConfirmationVisible: !emailConfirmationVisible,
      updateEmail: email,
      toggleEmailInput: false,
    }));
  };

  handleManagePlan = () => {
    const { history } = this.props;
    history.push(RENEW_SUBSCRIPTION_ROUTES.SELECT_PLANS);
  };

  render() {
    const {
      isLoading,
      user,
      toggleEmailInput,
      toggleNameInput,
      emailConfirmationVisible,
      updateEmail,
    } = this.state;
    const {
      first_name: firstName,
      last_name: lastName,
      business,
      email,
      login_count: loginCount,
      last_login: lastLogin,
      created_at: createdAt,
      categories,
      plan_subscription: planSubscription,
      plan,
      survey_created_count: surveyCreatedCount,
      survey_participant_count: surveyParticipantCount,
      issued_reward_count: issuedRewardCount,
      redeemed_reward_count: redeemedRewardCount,
      email_captured_count: emailCapturedCount,
    } = user;
    const { title: planTitle } = plan;
    const { formValues } = this.state;
    const {
      id,
      name,
      created_at: businessCreatedAt,
      primary_number,
    } = business;
    const { end_date: endDate } = planSubscription;
    const loginDate = formatUserDetailDate(lastLogin);
    const createdDate = formatUserDetailDate(createdAt);
    const businessCreatedDate = formatUserDetailDate(businessCreatedAt);
    const planEndDate = endDate ? formatUserDetailDate(endDate) : '-';
    const businessNameInitials = getNameInitials(name);
    const hideManageAccount =
      ((isProPlan() || isEssentialPlan()) && isPlanActive()) || isSuperAdmin();
    const userSince = getTimeSince(createdAt);
    const manageBusinessUrl = insertIdtoUrl(
      ROUTES.EDIT_BUSINESS_BASIC_INFO,
      id
    );
    const participantsPageLink = insertIdtoUrl(ROUTES.REWARD_ISSUED, id);

    const { status } = getPlanSubscriptionStatus() || {};
    const isActive = status === 'active';
    const isPendingCancellation = status === 'pending_cancellation';
    const isActiveOrPending = isActive || isPendingCancellation;

    if (isLoading) {
      return <Loading />;
    }

    return (
      <React.Fragment>
        <UpdateEmail
          isVisible={toggleEmailInput}
          toggleEmailConfirmationSent={this.updateEmail}
          toggleModal={this.handleEmailToggle}
          toggleEmailConfirmation={this.handleEmailToggle}
          toggleConfirmationSent={this.handleEmailConfirmationSent}
          email={email}
        />
        <EmailConfirmationSent
          email={updateEmail || email}
          isVisible={emailConfirmationVisible}
          toggleModal={this.handleEmailConfirmationSent}
        />
        <div className="container pr-0">
          <div className="row mr-0 mb-4 justify-content-center">
            <div className="col-lg-12 col-md-2 user-count justify-content-start user-name-icon mt-3 mb-2">
              <FontAwesomeIcon icon="user-circle" /> {firstName} {lastName}
            </div>
          </div>
          <div className="row mr-0 mb-4 justify-content-center">
            <div className="col-lg-3 col-md-10">
              <BusinessCardWrapper title="Business Information">
                <div className="user-dashboard-icon mt-4 mb-4">
                  <strong>{businessNameInitials}</strong>
                </div>
                <div className="user-information-card text-center mb-4">
                  <Link to={manageBusinessUrl}>
                    <h4>{name}</h4>
                  </Link>
                  <p className="text-capitalize">{`${planTitle} Account`}</p>
                </div>{' '}
              </BusinessCardWrapper>

              <BusinessCardWrapper title="Business Category">
                <CategoriesList categories={categories} />
              </BusinessCardWrapper>
            </div>
            <div className="col-lg-9 col-md-10 justify-content-end">
              <BusinessCardWrapper title="User Information">
                <div className="p-3">
                  <NameField
                    handleNameEdit={this.handleNameEdit}
                    first_name={firstName}
                    last_name={lastName}
                    formValues={formValues}
                    toggleNameInput={toggleNameInput}
                    updateName={this.updateName}
                    handleNameToggle={this.handleNameToggle}
                    handleChange={this.handleChange}
                  />
                  <hr />
                  <div className="row">
                    <div className="col-md-4 row-title mb-md-0 mb-2">
                      <FontAwesomeIcon icon="clock" className="mt-1" />
                      User Since{' '}
                    </div>
                    <div className="col-md-8">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          {createdDate} ({`${userSince}`})
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr />
                  <EmailField
                    formValues={formValues}
                    toggleEmailInput={toggleEmailInput}
                    handleEmailEdit={this.handleEmailEdit}
                    email={email}
                    updateEmail={this.updateEmail}
                    handleEmailToggle={this.handleEmailToggle}
                    handleChange={this.handleChange}
                  />
                  <hr />
                  <Field
                    icon="mobile-alt"
                    label="Phone"
                    value={primary_number}
                    editIcon={false}
                  />
                  <hr />
                  <div className="row">
                    <div className="col-md-4 row-title mb-md-0 mb-2">
                      <FontAwesomeIcon icon="store" className="mt-1" />
                      Business Account{' '}
                    </div>
                    <div className="col-md-8">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <Link className="p-0" to={manageBusinessUrl}>
                            {name}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr />
                  <Field
                    icon="sign-in-alt"
                    label="Last Login"
                    value={loginDate}
                    editIcon={false}
                  />
                  <hr />
                  <Field
                    icon="calculator"
                    label="Login Count"
                    value={loginCount}
                    editIcon={false}
                  />
                </div>
              </BusinessCardWrapper>

              <BusinessCardWrapper title="Account Information">
                <div className="p-3">
                  <div className="row">
                    <div className="col-md-4 row-title mb-md-0 mb-2">
                      <FontAwesomeIcon icon="columns" className="mt-1" />
                      Current Plan{' '}
                    </div>
                    <div className="col-md-8">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="text-capitalize">{`${planTitle} account`}</div>
                        <div>
                          <button
                            onClick={this.handleManagePlan}
                            className={`btn btn-sm btn-success float-right mr-3 btn-rounded ${hideManageAccount &&
                              'd-none'}`}
                          >
                            {isActiveOrPending
                              ? 'Upgrade Plan'
                              : 'Renew Subscription'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <hr />
                  <Field
                    icon="mobile-alt"
                    label="Phone"
                    value={primary_number}
                    editIcon={false}
                  />
                  <hr />
                  <Field
                    icon="clock"
                    label="Account Created"
                    value={businessCreatedDate}
                    editIcon={false}
                  />
                  <hr />
                  <Field
                    icon="clock"
                    label="Expiry Date"
                    value={planEndDate}
                    editIcon={false}
                  />
                </div>
              </BusinessCardWrapper>

              <BusinessCardWrapper title="Account Activity">
                <div className="p-3">
                  <div className="row">
                    <div className="col-md-4 row-title mb-md-0 mb-2">
                      <FontAwesomeIcon icon="file" className="mt-1" />
                      Survey Created{' '}
                    </div>
                    <div className="col-md-8">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <Link
                            className="p-0"
                            to={insertIdtoUrl(ROUTES.CUSTOM_SURVEY_LIST, id)}
                          >
                            {surveyCreatedCount}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr />

                  <div className="row">
                    <div className="col-md-4 row-title mb-md-0 mb-2">
                      <FontAwesomeIcon icon="trophy" className="mt-1" />
                      Issued Rewards{' '}
                    </div>
                    <div className="col-md-8">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <Link className="p-0" to={participantsPageLink}>
                            {issuedRewardCount}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr />

                  <div className="row">
                    <div className="col-md-4 row-title mb-md-0 mb-2">
                      <FontAwesomeIcon
                        icon="money-bill-wave-alt"
                        className="mt-1"
                      />
                      Reedeemed Rewards{' '}
                    </div>
                    <div className="col-md-8">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <Link className="p-0" to={participantsPageLink}>
                            {redeemedRewardCount}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr />

                  <div className="row">
                    <div className="col-md-4 row-title mb-md-0 mb-2">
                      <FontAwesomeIcon icon="users" className="mt-1" />
                      Survey Participants{' '}
                    </div>
                    <div className="col-md-8">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <Link className="p-0" to={participantsPageLink}>
                            {surveyParticipantCount}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr />

                  <div className="row">
                    <div className="col-md-4 row-title mb-md-0 mb-2">
                      <FontAwesomeIcon icon="envelope-open" className="mt-1" />
                      Email Captured{' '}
                    </div>
                    <div className="col-md-8">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <Link className="p-0" to={participantsPageLink}>
                            {emailCapturedCount}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </BusinessCardWrapper>

              <ManageSubscription
                plan_subscription={planSubscription}
                plan_status={plan}
                fetchUserDetails={this.fetchUserDetails}
              >
                <FontAwesomeIcon icon="cog" /> Manage Account
              </ManageSubscription>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

UserDetails.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

export default UserDetails;



// WEBPACK FOOTER //
// ./src/containers/UserDetails.js